import { useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";

/**
 * SchedaPreview — turista-side mock of a business profile card.
 * Split into MockBusinessCard (left) and BookingFlow (right) sub-components.
 */
const DATE_OPTIONS = ["Ven 11 lug", "Sab 12 lug", "Dom 13 lug"];
const SLOT_OPTIONS = ["12:30", "13:00", "20:00", "20:30", "21:00"];
const PEOPLE_OPTIONS = ["2", "3", "4", "5", "6"];

export default function SchedaPreview() {
    const { t } = useI18n();
    const [step, setStep] = useState(0); // 0 = preview, 1 = pick, 2 = awaiting, 3 = confirmed
    const confirmTimer = useRef(null);

    useEffect(() => () => clearTimeout(confirmTimer.current), []);

    const submitRequest = () => {
        setStep(2);
        confirmTimer.current = setTimeout(() => setStep(3), 1400);
    };

    return (
        <section
            id="scheda-preview"
            data-testid="scheda-preview"
            data-reveal="up"
            style={{ padding: "96px 32px", background: "var(--itly-page)" }}
        >
            <div style={{ maxWidth: 1120, margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: 56 }}>
                    <div className="itly-badge">{t("cardPreview.badge")}</div>
                    <h2
                        style={{
                            fontFamily: "Fraunces, serif",
                            fontSize: "clamp(32px, 4vw, 52px)",
                            lineHeight: 1.05,
                            marginTop: 20,
                            marginBottom: 18,
                            color: "var(--itly-fg-strong)",
                            letterSpacing: "-0.01em",
                        }}
                    >
                        {t("cardPreview.title_a")}
                        <br />
                        <em style={{ color: "var(--itly-orange-500)", fontStyle: "italic" }}>
                            {t("cardPreview.title_b")}
                        </em>
                    </h2>
                    <p
                        style={{
                            maxWidth: 720,
                            margin: "0 auto",
                            fontFamily: "Inter, sans-serif",
                            fontSize: 16,
                            lineHeight: 1.65,
                            color: "var(--itly-muted)",
                        }}
                    >
                        {t("cardPreview.sub")}
                    </p>
                </div>

                <div
                    className="itly-card-preview-shell"
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1.15fr 1fr",
                        gap: 28,
                        alignItems: "start",
                    }}
                >
                    <MockBusinessCard t={t} onBook={() => setStep(1)} />
                    <BookingFlow t={t} step={step} onSubmit={submitRequest} />
                </div>

                <div
                    style={{
                        marginTop: 26,
                        textAlign: "center",
                        fontFamily: "Inter, sans-serif",
                        fontSize: 12,
                        color: "var(--itly-muted)",
                        fontStyle: "italic",
                    }}
                >
                    {t("cardPreview.disclaimer")}
                </div>
            </div>
        </section>
    );
}

const photoBadge = {
    padding: "5px 10px",
    borderRadius: 999,
    fontFamily: "Inter, sans-serif",
    fontSize: 11.5,
    fontWeight: 600,
    letterSpacing: "0.02em",
};

function MockBusinessCard({ t, onBook }) {
    return (
        <article
            data-testid="mock-business-card"
            style={{
                background: "var(--itly-surface)",
                borderRadius: 22,
                border: "1px solid var(--itly-border)",
                boxShadow: "var(--itly-card-shadow)",
                overflow: "hidden",
            }}
        >
            {/* Photo mock */}
            <div
                aria-hidden="true"
                style={{
                    height: 200,
                    background: "linear-gradient(135deg, #C9E4E7 0%, #7FB2B6 55%, #4B8B90 100%)",
                    position: "relative",
                }}
            >
                <div style={{ position: "absolute", top: 14, left: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ ...photoBadge, background: "rgba(15, 61, 62, 0.9)", color: "#EAF6E9" }}>
                        {t("cardPreview.verified")}
                    </span>
                    <span style={{ ...photoBadge, background: "var(--itly-orange-500)", color: "#fff" }}>
                        {t("cardPreview.gemBadge")}
                    </span>
                </div>
            </div>

            <div style={{ padding: "22px 24px 24px" }}>
                <div
                    style={{
                        fontFamily: "Inter, sans-serif",
                        fontSize: 11.5,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        color: "var(--itly-orange-500)",
                        fontWeight: 600,
                        marginBottom: 8,
                    }}
                >
                    {t("cardPreview.mockPlace")}
                </div>
                <h3
                    style={{
                        fontFamily: "Fraunces, serif",
                        fontSize: 26,
                        color: "var(--itly-fg-strong)",
                        margin: "0 0 10px",
                        letterSpacing: "-0.005em",
                    }}
                >
                    {t("cardPreview.mockName")}
                </h3>
                <p
                    style={{
                        fontFamily: "Inter, sans-serif",
                        fontSize: 14,
                        color: "var(--itly-muted)",
                        lineHeight: 1.55,
                        margin: "0 0 18px",
                    }}
                >
                    {t("cardPreview.mockDesc")}
                </p>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 8,
                        fontFamily: "Inter, sans-serif",
                        fontSize: 13,
                        marginBottom: 18,
                    }}
                >
                    <div style={{ color: "var(--itly-fg-strong)" }}>{t("cardPreview.hours")}</div>
                    <div style={{ color: "var(--itly-fg-strong)", textAlign: "right" }}>{t("cardPreview.avgPrice")}</div>
                    <div style={{ color: "#4E7F65", display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span
                            aria-hidden="true"
                            style={{ width: 8, height: 8, borderRadius: 999, background: "#4E7F65", display: "inline-block" }}
                        />
                        {t("cardPreview.crowdNow")}
                    </div>
                </div>

                {/* Reviews block */}
                <div style={{ background: "var(--itly-cream)", borderRadius: 12, padding: "12px 14px", marginBottom: 20 }}>
                    <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "var(--itly-fg-strong)", fontWeight: 600 }}>
                        ★ ★ ★ ★ ★ · {t("cardPreview.reviewsLabel")}
                    </div>
                    <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--itly-muted)", fontStyle: "italic", marginTop: 3 }}>
                        {t("cardPreview.reviewNote")}
                    </div>
                </div>

                {/* CTAs */}
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                        onClick={onBook}
                        data-testid="preview-book-cta"
                        style={{
                            flex: 1,
                            minWidth: 160,
                            padding: "12px 18px",
                            background: "var(--itly-orange-500)",
                            color: "#fff",
                            border: "none",
                            borderRadius: 999,
                            fontFamily: "Inter, sans-serif",
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: "pointer",
                        }}
                    >
                        {t("cardPreview.ctaBook")}
                    </button>
                    <button
                        data-testid="preview-report-cta"
                        style={{
                            padding: "12px 16px",
                            background: "transparent",
                            color: "var(--itly-muted)",
                            border: "1px solid var(--itly-border)",
                            borderRadius: 999,
                            fontFamily: "Inter, sans-serif",
                            fontSize: 13,
                            cursor: "pointer",
                        }}
                    >
                        ⚑ {t("cardPreview.ctaReport")}
                    </button>
                </div>
            </div>
        </article>
    );
}

function BookingFlow({ t, step, onSubmit }) {
    const [date, setDate] = useState("Sab 12 lug");
    const [slot, setSlot] = useState("20:30");
    const [people, setPeople] = useState(2);

    return (
        <div
            data-testid="preview-booking-flow"
            style={{
                background: "var(--itly-surface)",
                borderRadius: 22,
                border: "1px solid var(--itly-border)",
                boxShadow: "var(--itly-card-shadow)",
                padding: "26px 26px 24px",
                minHeight: 360,
                display: "flex",
                flexDirection: "column",
                gap: 18,
            }}
        >
            <div
                style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: 11.5,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "var(--itly-orange-500)",
                    fontWeight: 600,
                }}
            >
                {t("try.book.label")}
            </div>
            <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 22, margin: 0, color: "var(--itly-fg-strong)" }}>
                {t("try.book.title")}
            </h3>

            {step === 0 && (
                <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13.5, lineHeight: 1.6, color: "var(--itly-muted)" }}>
                    <div style={{ marginBottom: 6 }}>{t("try.book.step1")}</div>
                    <div style={{ marginBottom: 6 }}>{t("try.book.step2")}</div>
                    <div>{t("try.book.step3")}</div>
                </div>
            )}

            {step >= 1 && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                    <MiniField label={t("try.book.date")} value={date} onChange={setDate} options={DATE_OPTIONS} testId="book-date" />
                    <MiniField label={t("try.book.slot")} value={slot} onChange={setSlot} options={SLOT_OPTIONS} testId="book-slot" />
                    <MiniField label={t("try.book.people")} value={String(people)} onChange={(v) => setPeople(Number(v))} options={PEOPLE_OPTIONS} testId="book-people" />
                </div>
            )}

            {step === 1 && (
                <button
                    onClick={onSubmit}
                    data-testid="book-send"
                    style={{
                        padding: "12px 18px",
                        background: "var(--itly-teal-900)",
                        color: "#fff",
                        border: "none",
                        borderRadius: 999,
                        fontFamily: "Inter, sans-serif",
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: "pointer",
                        alignSelf: "flex-start",
                    }}
                >
                    {t("try.book.sendCta")} →
                </button>
            )}

            {step === 2 && (
                <div
                    data-testid="book-awaiting"
                    style={{
                        background: "var(--itly-cream)",
                        padding: "14px 18px",
                        borderRadius: 14,
                        fontFamily: "Inter, sans-serif",
                        fontSize: 14,
                        color: "var(--itly-fg-strong)",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 10,
                    }}
                >
                    <span aria-hidden="true">◌</span>
                    {t("try.book.awaiting")}
                </div>
            )}

            {step === 3 && (
                <div
                    data-testid="book-confirmed"
                    style={{
                        background: "var(--itly-surface-green)",
                        padding: "14px 18px",
                        borderRadius: 14,
                        fontFamily: "Inter, sans-serif",
                        fontSize: 14,
                        color: "var(--itly-green-ink)",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 10,
                        fontWeight: 600,
                    }}
                >
                    <span aria-hidden="true">✓</span>
                    {t("try.book.confirmed")}
                </div>
            )}

            <div
                style={{
                    marginTop: "auto",
                    fontFamily: "Inter, sans-serif",
                    fontSize: 11.5,
                    color: "var(--itly-muted)",
                    fontStyle: "italic",
                }}
            >
                {t("try.book.demoNote")}
            </div>
        </div>
    );
}

function MiniField({ label, value, onChange, options, testId }) {
    return (
        <label
            style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                fontFamily: "Inter, sans-serif",
                fontSize: 11.5,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "var(--itly-muted)",
                fontWeight: 600,
            }}
        >
            {label}
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                style={{
                    padding: "10px 12px",
                    background: "var(--itly-cream)",
                    border: "1px solid var(--itly-border)",
                    borderRadius: 10,
                    fontFamily: "Inter, sans-serif",
                    fontSize: 13.5,
                    color: "var(--itly-fg-strong)",
                    fontWeight: 500,
                }}
            >
                {options.map((o) => (
                    <option key={o} value={o}>
                        {o}
                    </option>
                ))}
            </select>
        </label>
    );
}
